// frontend/src/components/leads/archive/archiveCopy.js
//
// The lead-archive feature's ONE copy table (owner-approved archive copy,
// 2026-09, items 1-19; item 9 - archive everything shown - was withdrawn by
// the owner), Hebrew and English, VERBATIM. Every archive surface -
// the card's icon button, the details window's button, the clear-feed menu and
// its confirm dialog, the page snackbar, the archive view's header and its
// empty state - reads its words from here, so a wording change is a one-line
// diff a reviewer can hold against the approval.
//
// In-app noun is "הזדמנות/הזדמנויות", never "ליד". ASCII hyphens only.
//
// `{n}` / `{d}` are filled by `fillArchive`. Nothing here is composed
// from fragments: a Hebrew sentence with a number in it is one template.

const COPY = {
  he: {
    archive: "העבר לארכיון", // 1
    archiveTip: "העבר לארכיון - תוסתר מהפיד ותישמר בארכיון", // 2
    restore: "שחזר מהארכיון", // 3
    archivedOne: "ההזדמנות הועברה לארכיון", // 4
    restoredOne: "ההזדמנות שוחזרה לפיד", // 5
    undo: "ביטול", // 6
    error: "שגיאה בהעברה לארכיון, נסו שוב", // 7
    restoreError: "שגיאה בשחזור, נסו שוב", // 7b
    clearFeed: "ניקוי הפיד", // 8
    archiveOlder: "העבר לארכיון הזדמנויות ישנות מ-{d} ימים", // 10
    confirmTitle: "להעביר {n} הזדמנויות לארכיון?", // 11
    confirmTitleOne: "להעביר הזדמנות אחת לארכיון?", // 11b
    confirmBody:
      "הן יוסתרו מהפיד ומהמונים. הסימונים שלך נשמרים, ואפשר לשחזר בכל עת.", // 12
    confirmAction: "העבר לארכיון", // 13
    // The cancel wording every other confirm dialog in the app renders
    // (SupporterRequestDialog, GroupsSettings, the Security tab's dialogs).
    cancel: "ביטול",
    bulkDone: "{n} הזדמנויות הועברו לארכיון", // 14
    nothing: "אין הזדמנויות להעברה לארכיון", // 15
    capped: "הועברו {n} - אפשר לחזור על הפעולה כדי להמשיך", // 15b
    archiveLink: "ארכיון", // 16
    // The archive menu's first item - the one string the icon-trigger
    // redesign (2026-09-28) added.
    viewArchive: "צפייה בארכיון",
    archiveHeader: "ארכיון - {n} הזדמנויות", // 17
    archiveHeaderOne: "ארכיון - הזדמנות אחת", // 17b
    backToFeed: "חזרה לפיד", // 18
    archiveEmpty: "אין הזדמנויות בארכיון", // 19
    // Not new copy: the dashboard's existing skeleton label, reused as the
    // accessible name of the dialog's counting spinner.
    counting: "טוען הזדמנויות",
  },
  en: {
    archive: "Archive",
    archiveTip: "Archive - hide from your feed, kept in the archive",
    restore: "Restore",
    archivedOne: "Opportunity archived",
    restoredOne: "Opportunity restored to your feed",
    undo: "Undo",
    error: "Could not archive, please try again",
    restoreError: "Could not restore, please try again",
    clearFeed: "Clear feed",
    archiveOlder: "Archive opportunities older than {d} days",
    confirmTitle: "Archive {n} opportunities?",
    confirmTitleOne: "Archive 1 opportunity?",
    confirmBody:
      "They will be hidden from your feed and counters. Your marks are kept, and you can restore them any time.",
    confirmAction: "Archive",
    cancel: "Cancel",
    bulkDone: "{n} opportunities archived",
    nothing: "Nothing to archive",
    capped: "Archived {n} - run again to continue",
    archiveLink: "Archive",
    viewArchive: "View archive",
    archiveHeader: "Archive - {n} opportunities",
    archiveHeaderOne: "Archive - 1 opportunity",
    backToFeed: "Back to feed",
    archiveEmpty: "No archived opportunities",
    counting: "Loading opportunities",
  },
};

/** The lookbacks the "older than" items offer - the backend's allowlist. */
export const OLDER_THAN_DAYS = [7, 30, 90];

/**
 * @param {boolean} isHebrew
 * @returns {Record<string, string>} the language's table.
 */
export const archiveText = (isHebrew) => (isHebrew ? COPY.he : COPY.en);

/**
 * Substitute `{name}` placeholders. A missing value is left as its own
 * placeholder rather than printed as "undefined", so a wiring mistake shows up
 * in review instead of shipping as a broken sentence.
 *
 * @param {string} template
 * @param {Record<string, string|number>} [vars]
 * @returns {string}
 */
export function fillArchive(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (whole, key) =>
    vars[key] === undefined || vars[key] === null ? whole : String(vars[key])
  );
}

/**
 * THE singular rule: the approved n === 1 template when n is 1 (the plural one
 * with a 1 in it, "1 הזדמנויות", is not Hebrew), else the plural template -
 * still unfilled, so a caller that renders the number apart can split it.
 *
 * @param {string} plural template with `{n}`.
 * @param {string} one the approved n === 1 sentence.
 * @param {number} n
 * @returns {string}
 */
const pickCounted = (plural, one, n) => (Number(n) === 1 ? one : plural);

/**
 * Pick a count sentence and fill it.
 *
 * @param {string} plural template with `{n}`.
 * @param {string} one the approved n === 1 sentence.
 * @param {number} n
 * @returns {string}
 */
export function countedArchive(plural, one, n) {
  return fillArchive(pickCounted(plural, one, n), { n });
}

/** The confirm dialog's title for `n` leads (items 11 / 11b). */
export const confirmTitleFor = (n, isHebrew) => {
  const t = archiveText(isHebrew);
  return countedArchive(t.confirmTitle, t.confirmTitleOne, n);
};

/**
 * The archive view's heading for `n` archived leads (items 16 / 17 / 17b), in
 * pieces so the view can set the number apart: `before + count + after` is
 * exactly the approved sentence, and `count` is null when the sentence carries
 * no number (the singular one, or the bare "ארכיון").
 *
 * An unknown count - and zero - reads as the bare "ארכיון": the empty-archive
 * card right below already says so, and "ארכיון - 0 הזדמנויות" above it would
 * say it twice.
 *
 * @param {number|null|undefined} n
 * @param {boolean} isHebrew
 * @returns {{before: string, count: number|null, after: string}}
 */
export function archiveHeaderParts(n, isHebrew) {
  const t = archiveText(isHebrew);
  const known = typeof n === "number" && Number.isFinite(n) && n > 0;
  if (!known) return { before: t.archiveLink, count: null, after: "" };
  const template = pickCounted(t.archiveHeader, t.archiveHeaderOne, n);
  const [before, after] = template.split("{n}");
  return after === undefined
    ? { before, count: null, after: "" }
    : { before, count: n, after };
}

/**
 * The snackbar sentence for one finished bulk run.
 *
 * ONE archived lead reads the per-lead sentence (item 4): the plural template
 * with a 1 in it ("1 הזדמנויות") is not Hebrew, and item 4 is the approved way
 * to say exactly this.
 *
 * @param {{archived: number, capped: boolean}} result
 * @param {boolean} isHebrew
 * @returns {string}
 */
export function bulkResultMessage(result, isHebrew) {
  const t = archiveText(isHebrew);
  const n = Number(result?.archived) || 0;
  if (n <= 0) return t.nothing;
  if (result?.capped) return fillArchive(t.capped, { n });
  if (n === 1) return t.archivedOne;
  return fillArchive(t.bulkDone, { n });
}

export default COPY;
